import { useState } from 'react';
import { api } from '../api/client';

interface Props {
  userEmail: string | null;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteAccountModal({ userEmail, onClose, onDeleted }: Props) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = userEmail !== null && confirmText.trim().toLowerCase() === userEmail.toLowerCase();

  async function handleDelete() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await api.delete('/auth/me');
      onDeleted();
    } catch {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-sm font-bold text-red-600">Delete your account</p>
          <p className="text-xs text-stone-500 mt-1">
            This permanently deletes your account. If you own a family archive, all of its events, photos,
            audio, comments, members, and delegates are deleted too. This cannot be undone.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-medium text-stone-700">
            Type your email ({userEmail}) to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-3 py-2 text-sm text-stone-500 hover:text-stone-700">Cancel</button>
            <button
              onClick={handleDelete} disabled={!canSubmit || submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? 'Deleting…' : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
